import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  Paper,
  Box,
  Typography,
  MaterialUICore,
  MaterialIcons,
  TextField,
} from '@iclinic/design-system';
import useStyles from './ComplaintCard.style';
import { updateComplaint, removeComplaint } from '../state';
import { complaintsSelectors } from '../state/selectors';
import { StateTree } from 'features/medicalRecords/state';
import { Complaint } from '../types';
import { addSuggestionToFavorites } from '../state/suggestions';

const { useTheme } = MaterialUICore;

interface ComplaintProps {
  id: string;
}

interface IconIndicationProps {
  icon: React.ReactNode;
  action?: () => void;
}

const { IconButton, Collapse } = MaterialUICore;
const { Star, Delete, ExpandLess, ExpandMore } = MaterialIcons;

function ComplaintCard({ id }: ComplaintProps): JSX.Element {
  const complaintInstanceSelector = (state: StateTree) =>
    complaintsSelectors.selectById(state, id);
  const complaint = useSelector(complaintInstanceSelector) as Complaint;

  const [favoriteComplaint, setFavoriteComplaint] = useState(
    complaint.favorite,
  );
  const [collapse, setCollapse] = useState(true);
  const [description, setDescription] = useState('');

  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();
  const iconColor = theme.palette.grey[500];
  const primaryColor = theme.palette.primary.main;

  const timeoutRef = useRef<any>();

  const handleClickFavorites = () => {
    const favorite = !favoriteComplaint;
    setFavoriteComplaint(favorite);
    dispatch(addSuggestionToFavorites({ title: complaint.title, favorite }));
  };

  const handleChangeColapse = () => {
    setCollapse((prev) => !prev);
  };

  const handleRemove = () => dispatch(removeComplaint(id));

  const favoriteTitle = favoriteComplaint
    ? `Remover ${complaint.title} dos favoritos`
    : `Adicionar ${complaint.title} aos favoritos`;

  const iconIndication: IconIndicationProps[] = [
    {
      icon: (
        <Star
          htmlColor={favoriteComplaint ? primaryColor : iconColor}
          titleAccess={favoriteTitle}
        />
      ),
      action: handleClickFavorites,
    },
    {
      icon: (
        <Delete
          titleAccess={`Remover ${complaint.title}`}
          htmlColor={iconColor}
        />
      ),
      action: handleRemove,
    },
    {
      icon: collapse ? (
        <ExpandLess htmlColor={iconColor} titleAccess="Fechar" />
      ) : (
        <ExpandMore htmlColor={iconColor} titleAccess="Abrir" />
      ),
      action: handleChangeColapse,
    },
  ];

  const handleChangeDescription = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.currentTarget;

    setDescription(value);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      dispatch(
        updateComplaint({ id, changes: { description: value as string } }),
      );
    }, 500);
  };

  return (
    <Paper className={classes.card} variant="outlined">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography
          data-testid="titleComplaint"
          className={classes.title}
          onClick={handleChangeColapse}
          variant="h4"
          title={`Queixa: ${complaint.title}`}
        >
          {complaint?.title}
        </Typography>
        <Box>
          {iconIndication.map((iconIn, index) => (
            <IconButton key={index.toString()} onClick={iconIn.action}>
              {iconIn.icon}
            </IconButton>
          ))}
        </Box>
      </Box>
      <Collapse in={collapse}>
        <Box mt={2}>
          <TextField
            id="description"
            className={classes.iputTextArea}
            type="text"
            label="História da doença atual"
            multiline
            rows={3}
            rowsMax={4}
            onChange={handleChangeDescription}
            value={description}
          />
        </Box>
      </Collapse>
    </Paper>
  );
}

export default React.memo(ComplaintCard);
